import { Directive, ElementRef, Input, OnChanges } from '@angular/core'
import { ResponsiveSizes, ResponsiveSources } from './responsive-image.interface'
import { maybe } from 'typescript-monads'

const makeSrcset = (sources: ResponsiveSources): string => {
  const sourceStrings = sources.reduce((acc, val) => {
    return acc.concat(`${val.url} ${val.size}${val.unit}`)
  }, [] as readonly string[])

  return sourceStrings.join(', ')
}

const makeSizes = (sizes: ResponsiveSizes): string => {
  const defaultStr = `${sizes.default.size}${sizes.default.unit}`
  return maybe(sizes.responsiveSizes)
    .map(xs => xs.reduce((acc, val) => acc.concat(`${val.mediaQuery} ${val.size}${val.unit}`), [] as readonly string[]))
    .map(xs => xs.concat(defaultStr))
    .map(xs => xs.join(', '))
    .valueOr(defaultStr)
}

/**
 * Produce a responsive HTML5 `<img>` element
 *
 * While `src` is not strictly required, it should be provided since older
 * browsers will use it as a fallback if they don't support `srcset`.
 *
 * @see https://developer.mozilla.org/en-US/docs/Learn/HTML/Multimedia_and_embedding/Responsive_images
 * @example
 * <img floResponsiveImg
 *      src="https://website.com/img.jpg"
 *      [sources]="imageSources"
 *      [sizes]="imageSizes"
 *      alt="A cool image" />
 */
@Directive({
  selector: 'img[floResponsiveImg]'
})
export class FloResponsiveImageDirective implements OnChanges {
  @Input() public sources: ResponsiveSources

  @Input() public sizes?: ResponsiveSizes

  constructor(private readonly el: ElementRef<HTMLImageElement>) {}

  ngOnChanges(): void {
    this.setImgAttributes()
  }

  private setImgAttributes() {
    this.el.nativeElement.srcset = makeSrcset(this.sources)
    maybe(this.sizes).tapSome(sizes => (this.el.nativeElement.sizes = makeSizes(sizes)))
  }
}
